import { makeObservable, observable, runInAction, toJS } from "mobx";
import { EntityRepository } from "../entity_repository";

const DEBOUNCE_MS = 16;

export class BaseStreamConsumer {
    repositoryMapping: {[key: string]: EntityRepository<any>};
    resumeToken: string | null = null;
    observeFuncs: ((e: any) => any)[] = [];
    isInitialized: boolean = false;
    timeStarted: number = 0;

    _consumer: any | null = null;
    private _pendingEvents: any[] = [];
    private _debounceTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(repositoryMapping: {[key: string]: EntityRepository<any>}) {
        this.repositoryMapping = repositoryMapping;
        makeObservable(this, {
            resumeToken: observable,
            isInitialized: observable
        });
    }

    addObserveFunc(func: (e: any) => any) {
        this.observeFuncs.push(func);
    }

    start() {
        if (!this._consumer) {
            this.resumeToken = null;
            this.isInitialized = false;
            this._consumer = this._consume();
        }
    }

    stop() {
        if (this._consumer) {
            this._close();
            this._consumer.cancel()
            this._consumer = null;
        }
        if (this._debounceTimer !== null) {
            clearTimeout(this._debounceTimer);
            this._debounceTimer = null;
        }
        this._pendingEvents = [];
    }

    _close() {}

    async _consume() {}

    _processEvent(event: any) {
        this._pendingEvents.push(event);
        if (this._debounceTimer !== null) {
            clearTimeout(this._debounceTimer);
        }
        this._debounceTimer = setTimeout(() => {
            this._debounceTimer = null;
            const events = this._pendingEvents;
            this._pendingEvents = [];
            runInAction(() => {
                for (const e of events) {
                    this._applyEvent(e);
                }
            });
        }, DEBOUNCE_MS);
    }

    _applyEvent(event: any) {
        for (let func of this.observeFuncs) {
            func(event);
        }
        this.resumeToken = event.resume_token;
        if (event.event_type == "time") {
            let timeCurrent = +Date.now(),
                rtt = timeCurrent - this.timeStarted,
                timeDiff = Math.floor((event.value * 1000 + rtt / 2) - timeCurrent);
            if (rtt < 5000) {
                for (let repo in this.repositoryMapping) {
                    this.repositoryMapping[repo].setTimeDiff(timeDiff)
                }
            }
            return;
        }
        if (event.event_type == "initial_finished") {
            this.isInitialized = true;
            return;
        }
        if (event.event_type == "invalidate") {
            for (let repo in this.repositoryMapping) {
                this.repositoryMapping[repo].clear();
            }
            this.resumeToken = null;
            return;
        }
        if (!this.repositoryMapping[event.entity_type]) {
            return;
        }
        if (event.event_type == "delete") {
            event.entity.is_deleted = true;
        }
        this.repositoryMapping[event.entity_type].addRemote(event.entity);
    }

    serialize() {
        let events = [];
        for (let entityType in this.repositoryMapping) {
            for (let entity of this.repositoryMapping[entityType].all) {
                events.push({
                    "event_type": "initial",
                    "entity_type": entityType,
                    "entity": toJS(entity.value)
                });
            }
        }
        return events;
    }
}
